import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import quoteService from '../../services/quoteService';
import './AppLayout.css';

const HomeIcon = ({ active }) => (
    <svg aria-label="Home" fill={active ? "currentColor" : "none"} height="24" role="img" viewBox="0 0 24 24" width="24">
        <path d={active ? "M22 23h-6.001a1 1 0 0 1-1-1v-5.455a2.997 2.997 0 1 0-5.993 0V22a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V11.543a1.002 1.002 0 0 1 .31-.724l10-9.543a1.001 1.001 0 0 1 1.38 0l10 9.543a1.002 1.002 0 0 1 .31.724V22a1 1 0 0 1-1 1Z" : "M9.005 16.545a2.997 2.997 0 0 1 2.997-2.997A2.997 2.997 0 0 1 15 16.545V22h7V11.543L12 2 2 11.543V22h7.005Z"} stroke="currentColor" strokeLinejoin="round" strokeWidth={active ? "0" : "2"} />
    </svg>
);

const SearchIcon = ({ active }) => (
    <svg aria-label="Search" fill={active ? "currentColor" : "none"} height="24" role="img" viewBox="0 0 24 24" width="24">
        <path d="M19 10.5A8.5 8.5 0 1 1 10.5 2a8.5 8.5 0 0 1 8.5 8.5Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? "3" : "2"} />
        <line x1="16.511" x2="22" y1="16.511" y2="22" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? "3" : "2"} />
    </svg>
);

const CreateIcon = ({ active }) => (
    <svg aria-label="New Post" fill={active ? "currentColor" : "none"} height="24" role="img" viewBox="0 0 24 24" width="24">
        <path d="M2 12v3.45c0 2.849.698 4.005 1.606 4.944.94.909 2.098 1.608 4.946 1.608h6.896c2.848 0 4.006-.7 4.946-1.608C21.302 19.455 22 18.3 22 15.45V8.552c0-2.849-.698-4.006-1.606-4.945C19.454 2.7 18.296 2 15.448 2H8.552c-2.848 0-4.006.699-4.946 1.607C2.698 4.547 2 5.703 2 8.552Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        <line x1="6.545" x2="17.455" y1="12.001" y2="12.001" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        <line x1="12.003" x2="12.003" y1="6.545" y2="17.455" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
);

const ProfileIcon = ({ active }) => (
    <svg aria-label="Profile" fill={active ? "currentColor" : "none"} height="24" role="img" viewBox="0 0 24 24" width="24">
        <circle cx="12.004" cy="8.5" fill="none" r="5.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? "0" : "2"} />
        {active && <circle cx="12.004" cy="8.5" r="5.5" />}
        <path d="M20.004 22c0-4.418-3.582-8-8-8s-8 3.582-8 8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? "0" : "2"} />
        {active && <path d="M20.004 22c0-4.418-3.582-8-8-8s-8 3.582-8 8H20.004Z" />}
    </svg>
);

const MoreIcon = ({ active }) => (
    <svg aria-label="More" fill={active ? "currentColor" : "none"} height="24" role="img" viewBox="0 0 24 24" width="24">
        <line x1="3" x2="21" y1="4" y2="4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? "3" : "2"} />
        <line x1="3" x2="21" y1="12" y2="12" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? "3" : "2"} />
        <line x1="3" x2="21" y1="20" y2="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? "3" : "2"} />
    </svg>
);

const QuoteIcon = ({ active, hasBadge, count }) => (
    <div className="nav-icon-wrapper">
        <svg aria-label="Quotes" fill={active ? "currentColor" : "none"} height="24" role="img" viewBox="0 0 24 24" width="24">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth={active ? "3" : "2"} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {hasBadge && <span className="sidebar-notification-dot"></span>}
    </div>
);

const AppLayout = ({ children, noPadding = false }) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const { logout, user } = useAuth();
    const { t, i18n } = useTranslation();
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [totalUnread, setTotalUnread] = useState(0);

    // Fetch unread count for sidebar badge
    useEffect(() => {
        if (!user?.profileId) return;

        const fetchUnreadCount = async () => {
            try {
                const response = await quoteService.getQuoteUpdates();
                const quotes = response.quotes || [];
                const total = quotes.reduce((sum, q) => sum + (q.unreadCount || 0), 0);
                setTotalUnread(total);
            } catch (error) {
                console.error('Error fetching unread count for sidebar:', error);
            }
        };

        // Initial fetch
        fetchUnreadCount();

        // Poll every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);

        return () => clearInterval(interval);
    }, [user?.profileId]);

    const toggleLanguage = () => {
        const nextLang = i18n.language === 'pt' ? 'en' : 'pt';
        i18n.changeLanguage(nextLang);
    };

    // Helper to determine active state
    const isActive = (path) => currentPath === path;

    const handleLogout = () => {
        logout();
    };

    const toggleMenu = (e) => {
        if (e) e.preventDefault();
        setShowMoreMenu(!showMoreMenu);
    };

    const NavContent = () => (
        <>
            <Link to="/home" className={`nav-item ${isActive('/home') ? 'active' : ''}`} onClick={() => setShowMoreMenu(false)}>
                <div className="nav-icon"><HomeIcon active={isActive('/home')} /></div>
                <span className="nav-label desktop-only">{t('home')}</span>
            </Link>

            <Link to="/explore" className={`nav-item ${isActive('/explore') ? 'active' : ''}`} onClick={() => setShowMoreMenu(false)}>
                <div className="nav-icon"><SearchIcon active={isActive('/explore')} /></div>
                <span className="nav-label desktop-only">{t('search_label')}</span>
            </Link>

            {user?.role !== 'client' && (
                <Link to="/artworks/manage" className={`nav-item ${isActive('/artworks/manage') ? 'active' : ''}`} onClick={() => setShowMoreMenu(false)}>
                    <div className="nav-icon"><CreateIcon active={isActive('/artworks/manage')} /></div>
                    <span className="nav-label desktop-only">{t('create_label')}</span>
                </Link>
            )}

            <Link to="/profile/edit" className={`nav-item ${isActive('/profile/edit') ? 'active' : ''}`} onClick={() => setShowMoreMenu(false)}>
                <div className="nav-icon"><ProfileIcon active={isActive('/profile/edit')} /></div>
                <span className="nav-label desktop-only">{t('profile_label')}</span>
            </Link>

            {user && (
                <Link
                    to={user?.role === 'client' ? "/my-quotes" : "/artist/quotes"}
                    className={`nav-item ${isActive('/artist/quotes') || isActive('/my-quotes') ? 'active' : ''}`}
                    onClick={() => setShowMoreMenu(false)}
                >
                    <div className="nav-icon">
                        <QuoteIcon active={isActive('/artist/quotes') || isActive('/my-quotes')} hasBadge={totalUnread > 0} count={totalUnread} />
                    </div>
                    <span className="nav-label desktop-only">
                        {user?.role === 'client' ? t('my_quote_requests', 'Minhas Solicitações') : t('quotes', 'Solicitações')}
                    </span>
                    {totalUnread > 0 && <span className="sidebar-badge-text desktop-only">{totalUnread}</span>}
                </Link>
            )}

            <div className={`nav-item ${showMoreMenu ? 'active' : ''}`} onClick={toggleMenu}>
                <div className="nav-icon"><MoreIcon active={showMoreMenu} /></div>
                <span className="nav-label desktop-only">{t('more_label')}</span>
            </div>
        </>
    );

    return (
        <div className="app-layout">
            {/* Desktop Sidebar */}
            <nav className="sidebar">
                <div className="sidebar-logo" onClick={() => (window.location.href = '/')}>
                    <span style={{
                        fontFamily: "'Courier Prime', monospace",
                        fontWeight: '700',
                        color: 'var(--color-primary)',
                        letterSpacing: '1px'
                    }}>NOVAINK</span>
                </div>
                <div className="sidebar-nav">
                    <NavContent />
                </div>

                {showMoreMenu && (
                    <>
                        <div className="menu-overlay desktop-only-overlay" onClick={() => setShowMoreMenu(false)}></div>
                        <div className="more-menu-dropdown desktop">
                            {user?.permissions?.includes('sys.admin') && (
                                <Link to="/admin/verification" className="menu-item" onClick={() => setShowMoreMenu(false)}>
                                    📋 {t('verification_requests') || 'Verification Requests'}
                                </Link>
                            )}
                            <button className="menu-item" onClick={toggleLanguage}>
                                🌐 {t('language')}: {i18n.language === 'pt' ? 'PT' : 'EN'}
                            </button>
                            <Link to="/settings" className="menu-item" onClick={() => setShowMoreMenu(false)}>⚙️ {t('settings')}</Link>
                            <button className="menu-item danger" onClick={() => { handleLogout(); setShowMoreMenu(false); }}>
                                🚪 {t('logout')}
                            </button>
                        </div>
                    </>
                )}
            </nav>

            {/* Main Content */}
            <main className={`main-content ${noPadding ? 'no-padding' : ''}`}>
                {children}
            </main>

            {/* Mobile Bottom Nav */}
            <nav className="bottom-nav">
                <Link to="/home" className="bottom-nav-item">
                    <HomeIcon active={isActive('/home')} />
                </Link>
                <Link to="/explore" className="bottom-nav-item">
                    <SearchIcon active={isActive('/explore')} />
                </Link>
                {user?.role !== 'client' && (
                    <Link to="/artworks/manage" className="bottom-nav-item">
                        <CreateIcon active={isActive('/artworks/manage')} />
                    </Link>
                )}
                <Link to="/profile/edit" className="bottom-nav-item">
                    <ProfileIcon active={isActive('/profile/edit')} />
                </Link>
                {user && (
                    <Link to={user?.role === 'client' ? "/my-quotes" : "/artist/quotes"} className="bottom-nav-item">
                        <QuoteIcon active={isActive('/artist/quotes') || isActive('/my-quotes')} hasBadge={totalUnread > 0} count={totalUnread} />
                    </Link>
                )}
                <div className="bottom-nav-item" onClick={toggleMenu}>
                    <MoreIcon active={showMoreMenu} />
                </div>
            </nav>

            {showMoreMenu && (
                <>
                    <div className="menu-overlay" onClick={() => setShowMoreMenu(false)}></div>
                    <div className="more-menu-dropdown mobile">
                        {user?.permissions?.includes('sys.admin') && (
                            <Link to="/admin/verification" className="menu-item" onClick={() => setShowMoreMenu(false)}>
                                📋 {t('verification_requests') || 'Verification Requests'}
                            </Link>
                        )}
                        <button className="menu-item" onClick={toggleLanguage}>
                            🌐 {t('language')}: {i18n.language === 'pt' ? 'PT' : 'EN'}
                        </button>
                        <Link to="/settings" className="menu-item" onClick={() => setShowMoreMenu(false)}>⚙️ {t('settings')}</Link>
                        <button className="menu-item danger" onClick={() => { handleLogout(); setShowMoreMenu(false); }}>🚪 {t('logout')}</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AppLayout;
